import React, { useCallback, useMemo } from 'react';

import { Box, HStack, Typography } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import { useStoreContext } from 'features/store';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import { logAddRecentItemToCartEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { routes } from 'utils/routing';

import { MinimalRecentOrderCard } from './minimal-recent-order-card';
import { MinimalRecentOrderFavoriteButton } from './minimal-recent-order-favorite-button';
import { MinimalRecentOrderData } from './types';
import { useMinimalRecentEntryAvailability } from './use-minimal-recent-entry-availability';

const Header = HStack.withConfig({
  paddingTop: '$1',
  paddingBottom: '$4',
  paddingX: '$3',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const visibleAvailabilityStatuses: ItemAvailabilityStatus[] = [
  ItemAvailabilityStatus.AVAILABLE,
  ItemAvailabilityStatus.STORE_NOT_SELECTED,
];

interface MinimalRecentOrderCardGroupProps {
  order: MinimalRecentOrderData;
}

const MinimalRecentOrderCardGroup: React.FC<MinimalRecentOrderCardGroupProps> = ({ order }) => {
  const { replace } = useRouter();
  const { store } = useStoreContext();
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const {
    availability,
    checkingAvailability,
    hasSomeAvailableEntry,
  } = useMinimalRecentEntryAvailability(order.minimalEntries);
  const orderNumber = order.orderNumberFormatted;
  const formattedDate = new Date(order.createdAt).toLocaleString(t('localeDateTime'), {
    month: 'long',
    day: 'numeric',
  });
  const visibleEntryRows = useMemo(
    () =>
      order.minimalEntries
        .map((entry, index) => ({
          entry,
          availability: availability[index],
        }))
        .filter(({ availability: entryAvailability }) => {
          if (!entryAvailability) {
            return false;
          }

          return visibleAvailabilityStatuses.includes(entryAvailability.itemAvailabilityStatus);
        }),
    [availability, order.minimalEntries]
  );

  const handleReorder = useCallback(() => {
    const availableEntries = visibleEntryRows
      .filter(
        ({ availability: entryAvailability }) =>
          entryAvailability?.itemAvailabilityStatus === ItemAvailabilityStatus.AVAILABLE
      )
      .map(({ entry }) => entry);

    const addedEntries = availableEntries.filter(entry => {
      const added = addMappedEntryToCart(entry);
      if (added) {
        logAddRecentItemToCartEvent(entry?.name ?? '', store);
      }
      return added;
    });

    const [firstEntry] = addedEntries;
    if (firstEntry) {
      const message =
        addedEntries.length === 1
          ? firstEntry.quantity > 1
            ? t('addToCartMultipleSuccess', {
                itemName: firstEntry.name,
                quantity: firstEntry.quantity - 1,
              })
            : t('addToCartSuccess', { itemName: firstEntry.name })
          : t('cartUpdatedRecentReorder', 'Your cart was updated');

      toaster.addToCartToast(message, firstEntry.image);
    }

    if (addedEntries.length) {
      replace(routes.cart);
    }
  }, [addMappedEntryToCart, replace, store, visibleEntryRows]);

  const header = (
    <Header>
      <HStack alignItems="center">
        <Typography fontSize="sm" bold>
          {formattedDate}
        </Typography>
        <MinimalRecentOrderFavoriteButton order={order} />
        {orderNumber && (
          <Typography fontSize="sm" ml="$2">
            {t('order')} #{orderNumber}
          </Typography>
        )}
      </HStack>
      <ActionButton
        testID="reorder-button"
        onPress={handleReorder}
        disabled={!hasSomeAvailableEntry}
        size={ActionButtonSizes.SMALL}
        aria-label={
          orderNumber ? t('reorderWithOrderNumber', { ticketNumber: orderNumber }) : t('reorder')
        }
      >
        {t('reorder')}
      </ActionButton>
    </Header>
  );

  if (!visibleEntryRows.length) {
    return (
      <Box testID="recent-order-card-group" paddingBottom="$3">
        {header}
        <Box paddingX="$3" paddingBottom="$4">
          <Typography fontSize="sm">{t('itemNotAvailable')}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box testID="recent-order-card-group" paddingBottom="$3">
      {header}
      {visibleEntryRows.map(({ entry, availability: entryAvailability }, index) => (
        <MinimalRecentOrderCard
          key={entry.cartId ?? `${entry.id}-${index}`}
          entry={entry}
          availability={
            entryAvailability ?? {
              itemAvailabilityStatus: ItemAvailabilityStatus.UNAVAILABLE,
              dayParts: [],
            }
          }
          checkingAvailability={checkingAvailability}
        />
      ))}
    </Box>
  );
};

export default MinimalRecentOrderCardGroup;
